import dayjs, { Dayjs } from 'dayjs';

import { RateBenchmarkResponse } from '../api/Api';
import { Type } from './types';
import { formatDateTo24HourTimeString, stringToDate } from './time';
import { clampProposedRate } from '../pages/dentist/PostJobPage/PostJob.utils';

export type RateGuidanceState = 'low' | 'market' | 'strong';

export interface RateGuidanceCopy {
    title: string;
    description: string;
    historicalContext?: string;
    hint?: string;
}

export interface RateGuidanceHelperTextArgs {
    hasRole: boolean;
    hasLocation: boolean;
    hasShiftDate: boolean;
    hasShiftStartTime: boolean;
    benchmark?: RateBenchmarkResponse | null;
}

export interface RateBenchmarkRequestParams {
    jobType: number;
    province?: string;
    postalPrefix?: string;
    locationId?: number;
    shiftDate?: string;
    shiftStartTime?: string;
}

interface BenchmarkLocationLike {
    id?: number | string;
    province?: string;
    postalCode?: string;
}

interface BenchmarkShiftLike {
    shiftDate?: string;
    postedStart?: string;
}

interface BenchmarkJobLike {
    location?: BenchmarkLocationLike;
    locationId?: number | string;
    type?: number | string;
    startDate?: string;
    shifts?: BenchmarkShiftLike[];
}

interface LastJobLike {
    type?: number;
    proposedRate?: number;
    paymentTerms?: number | string;
    paymentMethod?: number | string;
}

interface ResolveSuggestedProposedRateArgs {
    jobType: 0 | 1;
    lastJob?: LastJobLike | null;
    hasLastJob: boolean;
    rateBenchmark?: RateBenchmarkResponse | null;
}

interface ResolveSuggestedProposedRateResult {
    proposedRate?: number;
    shouldApplyLastJobSettings: boolean;
    shouldResetPaymentSettings: boolean;
}

const getMarketScopePhrase = (benchmark: RateBenchmarkResponse | null): string => {
    const granularity = benchmark?.guidanceContext?.guidanceGranularity;

    if (granularity === 'province_exact' || granularity === 'province_broader') {
        return 'across your province';
    }

    if (granularity === 'regional_fallback') {
        return 'across similar shifts in this region';
    }

    return 'in your area';
};

const getHistoricalContext = (benchmark: RateBenchmarkResponse | null): string | undefined => {
    const typicalBookedRate = benchmark?.typicalBookedRate;
    if (!typicalBookedRate) {
        return undefined;
    }

    const granularity = benchmark?.guidanceContext?.guidanceGranularity;
    const bookingSentence = `Shifts like this one typically book at around $${typicalBookedRate}/hr.`;

    if (granularity === 'local_broader') {
        return `Exact matches for this shift are limited, so this guidance uses similar shifts in your area. ${bookingSentence}`;
    }

    if (granularity === 'province_exact' || granularity === 'province_broader') {
        return `Local shift history is limited, so this guidance uses similar shifts across your province. ${bookingSentence}`;
    }

    if (granularity === 'regional_fallback') {
        return `Shift-specific local history is limited, so this guidance uses broader regional benchmark data. ${bookingSentence}`;
    }

    return `Shifts like this one typically book at around $${typicalBookedRate}/hr in your area.`;
};

const getResolvedBenchmarkJobType = (
    benchmark: RateBenchmarkResponse | null | undefined,
    jobType?: 0 | 1
): 0 | 1 | undefined => {
    const resolvedJobType = jobType ?? benchmark?.guidanceContext?.jobType;
    return resolvedJobType === Type.Hygienist || resolvedJobType === Type.Assistant
        ? resolvedJobType
        : undefined;
};

export const getClampedRecommendedRate = (
    benchmark: RateBenchmarkResponse | null | undefined,
    jobType?: 0 | 1
): number | undefined => {
    if (benchmark?.recommendedRate === undefined) {
        return undefined;
    }

    const resolvedJobType = getResolvedBenchmarkJobType(benchmark, jobType);
    if (resolvedJobType === undefined) {
        return Number(benchmark.recommendedRate);
    }

    return clampProposedRate(Number(benchmark.recommendedRate), resolvedJobType);
};

export const getTalentMarketBenchmarkRate = (
    benchmark: RateBenchmarkResponse | null | undefined
): number | undefined => {
    const benchmarkRate = benchmark?.recommendedRate ?? benchmark?.suggestedRate;
    const normalizedBenchmarkRate = Number(benchmarkRate);

    if (!Number.isFinite(normalizedBenchmarkRate) || normalizedBenchmarkRate <= 0) {
        return undefined;
    }

    return Math.round(normalizedBenchmarkRate);
};

export const getActiveTalentLabel = (jobType: number | undefined): string => (
    Number(jobType) === Type.Assistant ? 'Active assistants' : 'Active hygienists'
);

export const hasRateGuidanceRole = (role: unknown): boolean => {
    if (role === undefined || role === null || (typeof role === 'string' && role.trim() === '')) {
        return false;
    }

    const jobType = Number(role);
    return jobType === Type.Hygienist || jobType === Type.Assistant;
};

export const getBenchmarkLocation = ({
    locations,
    locationId,
    selectedLocation,
}: {
    locations?: BenchmarkLocationLike[];
    locationId?: number | string | null;
    selectedLocation?: BenchmarkLocationLike | null;
}): BenchmarkLocationLike | undefined => {
    if (locationId === undefined || locationId === null || locationId === '') {
        return undefined;
    }

    const listLocation = locations?.find(location => String(location.id) === String(locationId));

    if (
        selectedLocation
        && String(selectedLocation.id) === String(locationId)
        && (selectedLocation.province || selectedLocation.postalCode || !listLocation)
    ) {
        return selectedLocation;
    }

    return listLocation;
};

export const getRateGuidanceState = (
    selectedRate: number | undefined,
    benchmark: RateBenchmarkResponse | null
): RateGuidanceState | null => {
    if (!benchmark?.guidanceAvailable || benchmark.lowMaxRate === undefined || benchmark.strongMinRate === undefined) {
        return null;
    }

    const normalizedRate = Number(selectedRate);
    if (!normalizedRate) {
        return null;
    }

    if (normalizedRate <= benchmark.lowMaxRate) {
        return 'low';
    }

    if (normalizedRate >= benchmark.strongMinRate) {
        return 'strong';
    }

    return 'market';
};

export const getRateGuidanceCopy = (
    state: RateGuidanceState | null,
    benchmark: RateBenchmarkResponse | null
): RateGuidanceCopy | null => {
    if (!state || !benchmark) {
        return null;
    }

    const isShortNotice = ['same_day', 'next_day'].includes(benchmark.guidanceContext?.leadTimeBucket || '');
    const marketScopePhrase = getMarketScopePhrase(benchmark);
    const historicalContext = getHistoricalContext(benchmark);

    switch (state) {
        case 'low':
            return {
                title: 'Low for this shift',
                description: isShortNotice
                    ? `Based on similar recently booked shifts ${marketScopePhrase}, this proposed rate is low for this short-notice shift and may get fewer offers.`
                    : `Based on similar recently booked shifts ${marketScopePhrase}, this proposed rate is low for this shift and may get fewer offers.`,
                historicalContext,
                hint: isShortNotice
                    ? 'Short-notice shifts usually need a more competitive proposed rate to attract offers.'
                    : 'Raising your proposed rate moves this shift closer to the competitive range.',
            };
        case 'market':
            return {
                title: 'Market rate',
                description: `This proposed rate is aligned with the competitive range for similar shifts ${marketScopePhrase}.`,
                historicalContext,
                hint: isShortNotice
                    ? 'For same-day and next-day shifts, Instabook and Boosting the shift can help even more.'
                    : undefined,
            };
        case 'strong':
            return {
                title: 'Strong rate',
                description: `This proposed rate is above the typical booked range for similar shifts ${marketScopePhrase} and gives this shift a stronger chance of getting offers quickly.`,
                historicalContext,
            };
        default:
            return null;
    }
};

export const getRateGuidanceHelperText = ({
    hasRole,
    hasLocation,
    hasShiftDate,
    hasShiftStartTime,
    benchmark,
}: RateGuidanceHelperTextArgs): string => {
    if (!hasRole || !hasLocation) {
        return 'Select the role, office location, shift date, and arrival time to see market guidance for this specific shift.';
    }

    if (!hasShiftDate) {
        return 'Select the shift date and arrival time to see market guidance for this specific shift.';
    }

    if (!hasShiftStartTime) {
        return 'Select the arrival time to see market guidance for this specific shift.';
    }

    if (benchmark && benchmark.guidanceAvailable === false) {
        return 'Market rate insights for your shift details are not available yet. Your proposed rate impacts the hourly rates talent sees when submitting an offer.';
    }

    return 'Market guidance for this specific shift will appear once enough details are selected.';
};

export const formatShiftStartTime = (value: unknown): string | undefined => {
    if (!value) {
        return undefined;
    }

    if (typeof value === 'string') {
        const trimmedValue = value.trim();
        if (/^\d{2}:\d{2}$/.test(trimmedValue)) {
            return trimmedValue;
        }

        const browserDateStringTime = trimmedValue.match(/\b(\d{1,2}):(\d{2})(?::\d{2})?\s+GMT[+-]\d{4}\b/);
        if (browserDateStringTime) {
            return `${browserDateStringTime[1].padStart(2, '0')}:${browserDateStringTime[2]}`;
        }
    }

    const parsed = dayjs(value as string);
    if (parsed.isValid()) {
        return parsed.format('HH:mm');
    }

    const fallback = dayjs(String(value), 'HH:mm', true);
    return fallback.isValid() ? fallback.format('HH:mm') : undefined;
};

export const buildRateBenchmarkRequestParams = ({
    location,
    locationId,
    role,
    startDate,
    from,
    allowLegacyFallback = true,
}: {
    location?: BenchmarkLocationLike;
    locationId?: number | string;
    role?: number | string;
    startDate?: Dayjs | null;
    from?: string;
    allowLegacyFallback?: boolean;
}): RateBenchmarkRequestParams | null => {
    if (locationId === undefined || locationId === null || locationId === '' || !hasRateGuidanceRole(role)) {
        return null;
    }

    const formattedShiftDate = startDate && dayjs(startDate).isValid()
        ? dayjs(startDate).format('YYYY-MM-DD')
        : undefined;
    const formattedShiftStartTime = formatShiftStartTime(from);
    const jobType = Number(role);

    if (formattedShiftDate && formattedShiftStartTime) {
        return {
            locationId: Number(locationId),
            jobType,
            shiftDate: formattedShiftDate,
            shiftStartTime: formattedShiftStartTime,
        };
    }

    if (formattedShiftDate) {
        return null;
    }

    if (!location?.province || !location?.postalCode) {
        return null;
    }

    if (!allowLegacyFallback) {
        return null;
    }

    return {
        province: location.province,
        postalPrefix: location.postalCode.charAt(0),
        jobType,
    };
};

export const buildRateBenchmarkRequestParamsFromJob = (
    job?: BenchmarkJobLike | null
): RateBenchmarkRequestParams | null => {
    if (!job) {
        return null;
    }

    const firstShift = job.shifts?.[0];
    const shiftDate = firstShift?.shiftDate || job.startDate;
    const rawShiftStartTime = firstShift?.postedStart;
    const normalizedShiftStartTime = rawShiftStartTime
        ? formatShiftStartTime(rawShiftStartTime)
            || (() => {
                const parsedShiftStart = shiftDate
                    ? stringToDate(rawShiftStartTime, shiftDate)
                    : stringToDate(rawShiftStartTime);

                return Number.isNaN(parsedShiftStart.getTime())
                    ? undefined
                    : formatDateTo24HourTimeString(parsedShiftStart);
            })()
        : undefined;

    return buildRateBenchmarkRequestParams({
        location: job.location,
        locationId: job.locationId,
        role: job.type,
        startDate: shiftDate ? dayjs(String(shiftDate)) : null,
        from: normalizedShiftStartTime,
    });
};

export const resolveSuggestedProposedRate = ({
    jobType,
    lastJob,
    hasLastJob,
    rateBenchmark,
}: ResolveSuggestedProposedRateArgs): ResolveSuggestedProposedRateResult => {
    const lastComparableRate = lastJob?.type === jobType
        ? clampProposedRate(lastJob?.proposedRate, jobType)
        : undefined;
    const recommendedRate = getClampedRecommendedRate(rateBenchmark, jobType);
    const hasShiftAwareGuidance = Boolean(
        rateBenchmark?.guidanceAvailable
        && recommendedRate !== undefined
        && rateBenchmark.lowMaxRate !== undefined
        && rateBenchmark.strongMinRate !== undefined
    );

    let proposedRate: number | undefined;
    if (hasShiftAwareGuidance) {
        const shouldKeepLastComparableRate = lastComparableRate !== undefined
            && lastComparableRate > Number(rateBenchmark?.lowMaxRate)
            && lastComparableRate < Number(rateBenchmark?.strongMinRate);

        proposedRate = shouldKeepLastComparableRate
            ? lastComparableRate
            : recommendedRate;
    } else if (lastComparableRate !== undefined) {
        proposedRate = lastComparableRate;
    } else if (!hasLastJob) {
        proposedRate = jobType === Type.Hygienist ? 50 : 25;
    }

    return {
        proposedRate,
        shouldApplyLastJobSettings: lastJob?.type === jobType,
        shouldResetPaymentSettings: !hasShiftAwareGuidance && hasLastJob && lastJob?.type !== jobType,
    };
};

/** Fallback used when a location has no usable payment history (T2-6750). */
export const FALLBACK_PAYMENT_TERMS = 1;
export const FALLBACK_PAYMENT_METHOD = 1;

const VALID_PAYMENT_TERMS = new Set([1, 5, 14, 30]);
const VALID_PAYMENT_METHODS = new Set([1, 2, 3, 4]);

export interface LocationPaymentDefaultsLike {
    paymentTerms?: number | string | null;
    paymentMethod?: number | string | null;
}

export interface ResolveLocationPaymentDefaultsArgs {
    /** False while lastDentistJob is still in flight — caller should leave fields empty. */
    hasResolvedLastJob: boolean;
    locationPaymentDefaults?: LocationPaymentDefaultsLike | null;
    lastJob?: LocationPaymentDefaultsLike | null;
}

/**
 * Location-scoped payment defaults for Find a Temp / Post Job.
 * Prefers locationPaymentDefaults (any Job.type at the location) over the
 * type-filtered lastJob payment fields. Invalid/NULL DB values fall back to
 * Within 24h + Pay Now.
 */
export const resolveLocationPaymentDefaults = ({
    hasResolvedLastJob,
    locationPaymentDefaults,
    lastJob,
}: ResolveLocationPaymentDefaultsArgs): { paymentTerms: number; paymentMethod: number } | null => {
    if (!hasResolvedLastJob) {
        return null;
    }

    const rawTerms = locationPaymentDefaults?.paymentTerms ?? lastJob?.paymentTerms;
    const rawMethod = locationPaymentDefaults?.paymentMethod ?? lastJob?.paymentMethod;

    const paymentTerms = VALID_PAYMENT_TERMS.has(Number(rawTerms))
        ? Number(rawTerms)
        : FALLBACK_PAYMENT_TERMS;
    const paymentMethod = VALID_PAYMENT_METHODS.has(Number(rawMethod))
        ? Number(rawMethod)
        : FALLBACK_PAYMENT_METHOD;

    return { paymentTerms, paymentMethod };
};
